import { DateFormat, DateTimeSettings, Timezone } from '@cdw-ae/common-data';
import { createFeatureSelector, createSelector } from '@ngrx/store';
import { UserData } from './user-identity-settings.models';
import {
  USER_IDENTITY_SETTINGS_FEATURE_KEY,
  UserIdentitySettingsState,
  userIdentitySettingsAdapter,
} from './user-identity-settings.reducer';

/**
 * Default date/time settings when customer settings are not available
 */
export const DEFAULT_CUSTOMER_SETTINGS: DateTimeSettings = {
  timezone: Timezone.EASTERN,
  dateFormat: DateFormat.US,
};

export const selectUserIdentitySettingsState =
  createFeatureSelector<UserIdentitySettingsState>(
    USER_IDENTITY_SETTINGS_FEATURE_KEY
  );

const { selectAll, selectEntities } =
  userIdentitySettingsAdapter.getSelectors();

export const selectUserIdentitySettingsLoaded = createSelector(
  selectUserIdentitySettingsState,
  (state: UserIdentitySettingsState) => state.loaded
);

export const selectUserIdentitySettingsError = createSelector(
  selectUserIdentitySettingsState,
  (state: UserIdentitySettingsState) => state.error
);

export const selectAllUserIdentitySettings = createSelector(
  selectUserIdentitySettingsState,
  (state: UserIdentitySettingsState) => selectAll(state)
);

export const selectUserIdentitySettingsEntities = createSelector(
  selectUserIdentitySettingsState,
  (state: UserIdentitySettingsState) => selectEntities(state)
);

export const selectSelectedId = createSelector(
  selectUserIdentitySettingsState,
  (state: UserIdentitySettingsState) => state.selectedId
);

export const selectEntity = createSelector(
  selectUserIdentitySettingsEntities,
  selectSelectedId,
  (entities, selectedId) => (selectedId ? entities[selectedId] : undefined)
);

export const selectCurrentUserData = createSelector(
  selectUserIdentitySettingsState,
  (state: UserIdentitySettingsState): UserData | null => state.userData
);

export const selectUserPermissions = createSelector(
  selectUserIdentitySettingsState,
  (state: UserIdentitySettingsState) => state.permissions
);

export const selectPermissionsLoading = createSelector(
  selectUserIdentitySettingsState,
  (state: UserIdentitySettingsState) => state.permissionsLoading
);

export const selectPermissionsError = createSelector(
  selectUserIdentitySettingsState,
  (state: UserIdentitySettingsState) => state.permissionsError
);

/**
 * Select customer timezone and date format settings from authentication claims
 * Returns default settings if user is not authenticated or claims are not loaded
 */
export const selectCustomerDateTimeSettings = createSelector(
  selectCurrentUserData,
  (userData): DateTimeSettings => {
    const customerSettings = userData?.claims?.core?.customer;

    if (!customerSettings) {
      return DEFAULT_CUSTOMER_SETTINGS;
    }

    return {
      timezone:
        (customerSettings.timezone as Timezone) ||
        DEFAULT_CUSTOMER_SETTINGS.timezone,
      dateFormat:
        (customerSettings.dateFormat as DateFormat) ||
        DEFAULT_CUSTOMER_SETTINGS.dateFormat,
    };
  }
);

/**
 * Select user email from authentication data
 */
export const selectUserEmail = createSelector(
  selectCurrentUserData,
  (userData): string => userData?.email || ''
);

/**
 * Select user display name from authentication data
 */
export const selectUserName = createSelector(
  selectCurrentUserData,
  (userData): string => userData?.name || ''
);

/**
 * Select customer ID from authentication claims
 */
export const selectCustomerId = createSelector(
  selectCurrentUserData,
  (userData): string => userData?.claims?.core?.customerId?.toString() || ''
);

/**
 * Select Firebase UID from authentication data.
 * NOT the database user id — see `selectDbUserId` for that.
 */
export const selectFbUserId = createSelector(
  selectCurrentUserData,
  (userData): string => userData?.uid || ''
);

/**
 * Select database user ID (integer FK to users table) from authentication claims.
 * Coerced to a real number: Firebase custom claims can come back as strings at
 * runtime, and the "My Jobs" Firestore query uses a strict-typed `==`, so an
 * uncoerced string id silently fails to match the numeric `createdByUserId` on
 * execution docs (mirrors `selectCustomerId`'s defensive coercion).
 */
export const selectDbUserId = createSelector(
  selectCurrentUserData,
  (userData): number => Number(userData?.claims?.core?.dbUserId) || 0
);
